import { useState, useEffect, useRef, useCallback, RefObject } from "react"
import { SVGElementSelection } from "../types"
import { findSelectableElement, assignTableToElement } from "../svg_utils"
import { normalizeSvgInContainer } from "@/components/plan/svg_dom_normalize"

interface UseSvgSelectionProps {
  containerRef: RefObject<HTMLDivElement>
  svgContent: string | null
  onPlanChange: (updatedSvg: string) => void
}

export const useSvgSelection = ({ containerRef, svgContent, onPlanChange }: UseSvgSelectionProps) => {
  const [selectedElements, setSelectedElements] = useState<SVGElementSelection[]>([])
  const [selectedTableId, setSelectedTableId] = useState<string | null>(null)
  const elementCounterRef = useRef(0)

  const ensureElementId = useCallback((element: SVGElement): string => {
    if (!element.id) {
      element.id = `tabb-el-${elementCounterRef.current++}`
    }
    return element.id
  }, [])

  const findElementById = useCallback(
    (id: string): SVGElement | null => {
      if (!containerRef.current) return null
      return containerRef.current.querySelector(`#${CSS.escape(id)}`) as SVGElement | null
    },
    [containerRef],
  )

  useEffect(() => {
    if (!containerRef.current || !svgContent) return

    const container = containerRef.current
    container.innerHTML = svgContent

    const svgElement = container.querySelector("svg")
    if (!svgElement) return

    normalizeSvgInContainer(container)

    const handleClick = (event: MouseEvent) => {
      const target = event.target as SVGElement
      if (!target || target.tagName === "svg") {
        setSelectedElements([])
        setSelectedTableId(null)
        return
      }

      if (target.hasAttribute("data-tabb-overlay") || target.closest("[data-tabb-overlay]")) {
        return
      }

      const isMultiSelect = event.ctrlKey || event.metaKey

      setSelectedElements((prevElements) => {
        const lastSelected = prevElements.length > 0 ? prevElements[prevElements.length - 1] : null
        const selectableElement = findSelectableElement(target, lastSelected)

        if (selectableElement) {
          event.stopPropagation()

          ensureElementId(selectableElement.element)

          const tableId = selectableElement.element.getAttribute("data-tabb-table")
          setSelectedTableId(tableId || null)

          if (isMultiSelect) {
            const existingIndex = prevElements.findIndex((el) => el.element === selectableElement.element)

            if (existingIndex >= 0) {
              return prevElements.filter((_, i) => i !== existingIndex)
            } else {
              return [...prevElements, selectableElement]
            }
          } else {
            return [selectableElement]
          }
        }
        return prevElements
      })
    }

    svgElement.addEventListener("click", handleClick as EventListener)

    return () => {
      svgElement.removeEventListener("click", handleClick as EventListener)
    }
  }, [svgContent, containerRef, ensureElementId])

  const handleTableAssignment = useCallback(
    (tableId: string | null) => {
      if (!containerRef.current || selectedElements.length === 0) return

      const container = containerRef.current
      const svgElement = container.querySelector("svg")
      if (!svgElement) return

      selectedElements.forEach((selection) => {
        const currentElement = selection.element.id ? findElementById(selection.element.id) : selection.element

        if (currentElement) {
          assignTableToElement(currentElement, tableId)
        }
      })

      setSelectedTableId(tableId)

      const svgClone = svgElement.cloneNode(true) as SVGElement
      const overlays = svgClone.querySelectorAll("[data-tabb-overlay]")
      overlays.forEach((overlay) => overlay.remove())
      const highlightOverlays = svgClone.querySelectorAll("[data-tabb-highlight-overlay]")
      highlightOverlays.forEach((overlay) => overlay.remove())

      const serializer = new XMLSerializer()
      const updatedSvg = serializer.serializeToString(svgClone)
      onPlanChange(updatedSvg)
    },
    [containerRef, selectedElements, findElementById, onPlanChange],
  )

  return {
    selectedElements,
    selectedTableId,
    handleTableAssignment,
    findElementById,
  }
}
